import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ModuleLink as Link } from '../../components/ModuleLink';
import { useApiClient } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { safeJson, fmtDate, surveyBadgeClass } from '../../utils';
import type { ChangeRequest, ChangeLog, SurveyLogData, LessonsLearned } from '../../types';
import Layout from '../../components/Layout';

export default function SponsorClosure() {
  const [searchParams] = useSearchParams();
  const cid = searchParams.get('cid') ?? '';
  const api = useApiClient();
  const { showToast } = useToast();

  const [loading, setLoading]       = useState(true);
  const [state, setState]           = useState<'form' | 'done' | 'confirmed'>('form');
  const [change, setChange]         = useState<ChangeRequest | null>(null);
  const [surveyLogs, setSurveyLogs] = useState<ChangeLog[]>([]);
  const [confirmed, setConfirmed]   = useState(false);
  const [comments, setComments]     = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');

  useEffect(() => {
    if (!cid) { setLoading(false); return; }
    async function load() {
      try {
        const [c, logs] = await Promise.all([
          api.publicGet<ChangeRequest>(`/changes/${encodeURIComponent(cid)}`),
          api.publicGet<ChangeLog[]>(`/changes/${encodeURIComponent(cid)}/logs?type=Survey Response`),
        ]);
        setChange(c as ChangeRequest);
        setSurveyLogs((logs as ChangeLog[]).filter(l => safeJson<Partial<SurveyLogData>>(l.logData, {}).status === 'Responded'));
        if ((c as ChangeRequest).sponsorSignoffDate) setState('done');
      } catch { showToast('Failed to load record.', 'error'); }
      finally { setLoading(false); }
    }
    load();
  }, [cid]);

  async function confirmClosure() {
    if (!confirmed) { setError('Please check the confirmation box.'); return; }
    setError('');
    setSubmitting(true);
    try {
      await api.publicPatch(`/changes/${encodeURIComponent(cid)}/close`, { sponsorSignoffDate: new Date().toISOString(), status: 'Closed', currentStep: 'Closed', closureDate: new Date().toISOString(), sponsorComments: comments.trim() });
      setState('confirmed');
    } catch { showToast('Submission failed.', 'error'); }
    finally { setSubmitting(false); }
  }

  const ll = safeJson<Partial<LessonsLearned>>(change?.lessonsLearned, {});
  const unresolvedConcerns = surveyLogs.filter(l => safeJson<Partial<SurveyLogData>>(l.logData, {}).unresolvedConcerns);

  return (
    <Layout maxWidth="form">
          <nav className="breadcrumb">
            <Link to="/" className="breadcrumb__link"><i className="fa-solid fa-house" aria-hidden="true" /> Home</Link>
            <span className="breadcrumb__sep">/</span>
            <span className="breadcrumb__current">Sponsor Closure</span>
          </nav>
          {loading && <div className="loading-state"><div className="spinner spinner--lg" /><span>Loading…</span></div>}
        {!loading && state === 'done' && (
          <div>
            <div className="state-card">
              <div style={{ fontSize: '3rem', color: 'var(--accent)' }}><i className="fa-solid fa-circle-check" aria-hidden="true" /></div>
              <h1 className="state-card__title">Already Confirmed</h1>
              <p className="state-card__body">This change closure has already been confirmed. Thank you.</p>
            </div>
            <Link to="/" className="btn btn--secondary" style={{ marginTop: 16 }}>
              <i className="fa-solid fa-house" aria-hidden="true" /> Back to Home
            </Link>
          </div>
        )}
        {!loading && state === 'confirmed' && (
          <div>
            <div className="alert alert--success">
              <span className="alert__icon"><i className="fa-solid fa-circle-check" aria-hidden="true" /></span>
              <div className="alert__body"><div className="alert__title">Change Formally Closed</div><p>This change has been formally closed. All parties have been notified.</p></div>
            </div>
            <Link to="/" className="btn btn--secondary" style={{ marginTop: 16 }}>
              <i className="fa-solid fa-house" aria-hidden="true" /> Back to Home
            </Link>
          </div>
        )}
        {!loading && state === 'form' && change && (
          <div>
            <span className="badge badge--primary">{change.changeId}</span>
            <h1 style={{ marginTop: 6, marginBottom: 4 }}>{change.changeName}</h1>
            <p className="text-muted">Change Lead: {change.leadName} · Go-Live: {fmtDate(change.confirmedGolive)}</p>
            <div className="divider" />

            {/* Change summary */}
            <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16 }}><i className="fa-solid fa-file-lines" style={{ color: 'var(--primary)' }} aria-hidden="true" /> Change Summary</h2>
            <div className="field-display-grid" style={{ marginBottom: 16 }}>
              <div className="field-display"><div className="field-display__label">Change Lead</div><div className="field-display__value">{change.leadName}</div></div>
              <div className="field-display"><div className="field-display__label">Go-Live Date</div><div className="field-display__value">{fmtDate(change.confirmedGolive)}</div></div>
              <div className="field-display"><div className="field-display__label">Survey Status</div><div className="field-display__value">{change.surveyStatus ? <span className={`badge ${surveyBadgeClass(change.surveyStatus)}`}>{change.surveyStatus}</span> : '—'}</div></div>
              <div className="field-display"><div className="field-display__label">Average Score</div><div className="field-display__value">{change.surveyScore != null ? `${change.surveyScore} / 10` : '—'}</div></div>
            </div>

            {/* Lessons learned */}
            <div className="divider" />
            <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16 }}><i className="fa-solid fa-lightbulb" style={{ color: 'var(--primary)' }} aria-hidden="true" /> Lessons Learned</h2>
            <div className="field-display-grid" style={{ marginBottom: 16 }}>
              {[['What went well?', ll.wentWell], ['What could be done better?', ll.improvements], ['Recommendations', ll.recommendations]].map(([l, v]) => v ? (
                <div key={l as string} className="field-display">
                  <div className="field-display__label">{l as string}</div>
                  <div className="field-display__value"><pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.875rem' }}>{v as string}</pre></div>
                </div>
              ) : null)}
            </div>

            {/* Unresolved concerns */}
            {unresolvedConcerns.length > 0 && (
              <>
                <div className="divider" />
                <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16 }}><i className="fa-solid fa-triangle-exclamation" style={{ color: 'var(--warn)' }} aria-hidden="true" /> Unresolved Concerns from Survey</h2>
                {unresolvedConcerns.map(l => {
                  const d = safeJson<Partial<SurveyLogData>>(l.logData, {});
                  return (
                    <div key={l.id} style={{ padding: '12px 14px', background: 'var(--warn-light)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 'var(--radius-sm)', marginBottom: 8 }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 4 }}>{l.loggedByName}</div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--ink-dim)' }}>{d.unresolvedConcerns}</div>
                    </div>
                  );
                })}
              </>
            )}

            {/* Closure form */}
            <div className="divider" />
            <h2 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: 16 }}><i className="fa-solid fa-stamp" style={{ color: 'var(--primary)' }} aria-hidden="true" /> Confirm Closure</h2>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="confirm-checkbox-label">
                <input type="checkbox" checked={confirmed} onChange={e => { setConfirmed(e.target.checked); setError(''); }} style={{ width: 16, height: 16, accentColor: 'var(--primary)', flexShrink: 0 }} />
                <span>I confirm this change is closed and the intended outcomes have been achieved to the degree reflected in the survey results.</span>
              </label>
              {error && <span className="form-error" style={{ marginTop: 8 }}><i className="fa-solid fa-circle-exclamation" aria-hidden="true" /> {error}</span>}
            </div>
            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label" htmlFor="sponsor-comments">Comments <span className="badge badge--muted" style={{ fontWeight: 400, marginLeft: 6 }}>Optional</span></label>
              <textarea id="sponsor-comments" className="form-control" rows={4} value={comments} onChange={e => setComments(e.target.value)} placeholder="Any final comments or observations…" />
            </div>
            <button className={`btn btn--primary btn--lg w-full${submitting ? ' is-loading' : ''}`} onClick={confirmClosure} disabled={!confirmed || submitting}>
              <i className="fa-solid fa-circle-check" aria-hidden="true" /> Confirm Closure
            </button>
          </div>
        )}
    </Layout>
  );
}
